import { ref } from "vue";
import type { ViewportState, GridInfo } from "../types";
import { Stage } from "konva/lib/Stage";

export function useViewport() {
  // 初始视图状态
  const initialViewState = ref<ViewportState | null>(null);

  // 保存初始视图状态
  const saveInitialState = (state: ViewportState) => {
    initialViewState.value = { ...state };
  };

  // 视图边界限制函数
  const constrainViewport = (
    x: number,
    y: number,
    scale: number,
    stageWidth: number,
    stageHeight: number,
    gridInfo: GridInfo
  ) => {
    const { gridWidth, gridHeight, startX, startY } = gridInfo;
    const scaledGridWidth = gridWidth * scale;
    const scaledGridHeight = gridHeight * scale;
    const scaledStartX = startX * scale;
    const scaledStartY = startY * scale;

    // 简单的边界限制：不让网格完全移出视口
    const padding = 100; // 至少保留100像素在视口内

    // 计算网格在舞台坐标系中的实际位置
    const gridLeft = scaledStartX + x;
    const gridRight = gridLeft + scaledGridWidth;
    const gridTop = scaledStartY + y;
    const gridBottom = gridTop + scaledGridHeight;

    let newX = x;
    let newY = y;

    // 防止网格完全移出视口
    if (gridRight < padding) {
      newX = padding - scaledGridWidth - scaledStartX;
    }
    if (gridLeft > stageWidth - padding) {
      newX = stageWidth - padding - scaledStartX;
    }

    if (gridBottom < padding) {
      newY = padding - scaledGridHeight - scaledStartY;
    }
    if (gridTop > stageHeight - padding) {
      newY = stageHeight - padding - scaledStartY;
    }

    return { x: newX, y: newY };
  };

  // 重置视图函数
  const resetView = (stage: Stage) => {
    if (!stage || !initialViewState.value) return false;

    // 获取当前layer和网格信息
    const layer = stage.children[0] as unknown as { gridInfo: GridInfo };
    if (!layer || !layer.gridInfo) {
      // 如果没有网格信息，使用简单的居中
      stage.scale({
        x: initialViewState.value.scale,
        y: initialViewState.value.scale,
      });
      stage.x(0);
      stage.y(0);
      stage.batchDraw();
      return true;
    }

    const gridInfo = layer.gridInfo as GridInfo;
    const currentStageWidth = stage.width();
    const currentStageHeight = stage.height();

    // 使用初始缩放比例
    stage.scale({
      x: initialViewState.value.scale,
      y: initialViewState.value.scale,
    });

    // 根据当前舞台尺寸重新计算网格居中位置
    const newCenterX = currentStageWidth / 2;
    const newCenterY = currentStageHeight / 2;
    const newStartX = newCenterX - gridInfo.gridWidth / 2;
    const newStartY = newCenterY - gridInfo.gridHeight / 2;

    // 计算需要的偏移量让网格居中
    const offsetX = newStartX - gridInfo.startX;
    const offsetY = newStartY - gridInfo.startY;

    stage.x(offsetX);
    stage.y(offsetY);

    stage.batchDraw();
    return true;
  };

  // 计算缩放后的位置
  const calculateZoomPosition = (
    pointer: { x: number; y: number },
    oldScale: number,
    newScale: number,
    stage: Stage
  ) => {
    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    return {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    };
  };

  return {
    initialViewState,
    saveInitialState,
    constrainViewport,
    resetView,
    calculateZoomPosition,
  };
}
